import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import companyBranchesApi from "../../api/companyBranchesApi";
import employeesApi from "../../api/employeesApi";
import questionnaireApplicationsApi from "../../api/questionnaireApplicationsApi";
import inbodyApi from "../../api/inbodyApi";
import labResultsApi from "../../api/labResultsApi";
import MonthYearPicker from "../../components/MonthYearPicker/MonthYearPicker";
import EmployeeManagerModal from "./EmployeeManagerModal";
import ApplicationEditModal from "./ApplicationEditModal";
import styles from "./BranchConfigurationPage.module.css";

const SURVEYS = [
  { name: "hra", label: "HRA" },
  { name: "healthBurnout", label: "HRA & Burnout" },
  { name: "estresBurnout", label: "Estrés y Burnout" },
  { name: "nom035", label: "NOM-035", nom: true },
  { name: "nom035SecI", label: "NOM-035 Guía I", nom: true },
  { name: "generalHealtQuestionarie", label: "Evaluación Integral de Bienestar Emocional" },
];

function AreaEditor({ areas, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const add = () => {
    const area = input.trim();
    if (!area || areas.includes(area)) return;
    onChange([...areas, area]);
    setInput("");
  };
  return (
    <div>
      <div className={styles.inlineRow}>
        <input
          className={styles.input}
          placeholder={placeholder}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              add();
            }
          }}
        />
        <button type="button" className={styles.secondaryButton} onClick={add}>Agregar</button>
      </div>
      <div className={styles.chips}>
        {areas.map((area) => (
          <span key={area} className={styles.chip}>
            {area}<button type="button" onClick={() => onChange(areas.filter((item) => item !== area))}>×</button>
          </span>
        ))}
      </div>
    </div>
  );
}

function SurveySection({ survey, branch, branchEmployeesCount, onCreated, onEdit }) {
  const existing = branch.surveys?.find((item) => item.name === survey.name)?.applications || [];
  const [applicationName, setApplicationName] = useState("");
  const [jobAreas, setJobAreas] = useState([]);
  const [employeesFile, setEmployeesFile] = useState(null);
  const [useBranchJobAreas, setUseBranchJobAreas] = useState(false);
  const [useBranchEmployees, setUseBranchEmployees] = useState(false);
  const [usesEmployeeCredentials, setUsesEmployeeCredentials] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const createApplication = async () => {
    if (!applicationName.trim()) {
      setError("Escribe el nombre de la aplicación.");
      return;
    }
    if (survey.nom) {
      if (!useBranchJobAreas && !jobAreas.length) {
        setError("Agrega al menos un área o usa las áreas de la sucursal.");
        return;
      }
      if (!useBranchEmployees && !employeesFile) {
        setError("Sube una lista de empleados o usa los empleados de la sucursal.");
        return;
      }
    }

    setSaving(true);
    setError("");
    try {
      await questionnaireApplicationsApi.create({
        branchId: branch._id,
        applicationName: applicationName.trim(),
        typeApplication: survey.name,
        jobAreas,
        employeesFile,
        useBranchJobAreas,
        useBranchEmployees,
        usesEmployeeCredentials,
      });
      setApplicationName("");
      setJobAreas([]);
      setEmployeesFile(null);
      setUseBranchJobAreas(false);
      setUseBranchEmployees(false);
      setUsesEmployeeCredentials(false);
      await onCreated();
    } catch (requestError) {
      console.error("Error creando aplicación:", requestError);
      setError(requestError.response?.data?.message || "No se pudo crear la aplicación.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <article className={styles.surveyCard}>
      <div className={styles.surveyHeader}>
        <div>
          <h3>{survey.label}</h3>
          <p>{existing.length} {existing.length === 1 ? "aplicación" : "aplicaciones"}</p>
        </div>
        <span className={existing.length ? styles.activeBadge : styles.availableBadge}>
          {existing.length ? "Configurado" : "Disponible"}
        </span>
      </div>

      {existing.length > 0 && (
        <ul className={styles.applicationList}>
          {existing.map((application) => (
            <li key={application.questionnaireApplicationId}>
              <button
                type="button"
                className={styles.applicationListButton}
                onClick={() => onEdit({
                  ...application,
                  typeApplication: survey.name,
                  surveyLabel: survey.label,
                })}
              >
                <span>{application.applicationName}</span>
                <small>Editar</small>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className={styles.applicationForm}>
        <label>Nombre de la nueva aplicación</label>
        <input
          className={styles.input}
          value={applicationName}
          placeholder={`Ej. ${survey.label} 2026`}
          onChange={(event) => setApplicationName(event.target.value)}
        />

        {survey.nom && (
          <div className={styles.nomFields}>
            <div>
              <label>Áreas de trabajo de esta aplicación</label>
              <p>
                Copia las áreas generales y, si lo necesitas, agrega áreas exclusivas para esta aplicación.
              </p>
              <button
                type="button"
                className={`${styles.sourceButton} ${useBranchJobAreas ? styles.sourceButtonActive : ""}`}
                aria-pressed={useBranchJobAreas}
                onClick={() => setUseBranchJobAreas((value) => !value)}
              >
                {useBranchJobAreas ? "✓ Usar datos de la sucursal" : "Usar datos de la sucursal"}
                <span>{branch.jobAreas?.length || 0} áreas disponibles</span>
              </button>
              <AreaEditor
                areas={jobAreas}
                onChange={setJobAreas}
                placeholder={useBranchJobAreas ? "Agregar área extra" : "Área de esta aplicación"}
              />
            </div>
            <div>
              <label>Lista de empleados de esta aplicación</label>
              <p>
                Elige cómo se identificarán los empleados al abrir el cuestionario.
              </p>
              <div className={styles.identityOptions}>
                <button
                  type="button"
                  className={`${styles.sourceButton} ${!usesEmployeeCredentials ? styles.sourceButtonActive : ""}`}
                  aria-pressed={!usesEmployeeCredentials}
                  onClick={() => {
                    setUsesEmployeeCredentials(false);
                    setEmployeesFile(null);
                  }}
                >
                  Correo electrónico
                  <span>Columna emails</span>
                </button>
                <button
                  type="button"
                  className={`${styles.sourceButton} ${usesEmployeeCredentials ? styles.sourceButtonActive : ""}`}
                  aria-pressed={usesEmployeeCredentials}
                  onClick={() => {
                    setUsesEmployeeCredentials(true);
                    setUseBranchEmployees(false);
                    setEmployeesFile(null);
                  }}
                >
                  Número y código
                  <span>Columnas employee_number y employee_code</span>
                </button>
              </div>
              {!usesEmployeeCredentials && (
                <button
                  type="button"
                  className={`${styles.sourceButton} ${useBranchEmployees ? styles.sourceButtonActive : ""}`}
                  aria-pressed={useBranchEmployees}
                  onClick={() => setUseBranchEmployees((value) => !value)}
                >
                  {useBranchEmployees ? "✓ Usar datos de la sucursal" : "Usar datos de la sucursal"}
                  <span>{branchEmployeesCount} empleados disponibles</span>
                </button>
              )}
              <label className={styles.optionalLabel}>
                {!usesEmployeeCredentials && useBranchEmployees
                  ? "Archivo de empleados adicionales (opcional)"
                  : "Archivo de empleados"}
              </label>
              <input
                key={usesEmployeeCredentials ? "credentials" : "email"}
                type="file"
                accept=".csv,.xlsx"
                onChange={(event) => setEmployeesFile(event.target.files[0] || null)}
              />
              <a
                className={styles.templateLink}
                href={usesEmployeeCredentials
                  ? "/template/employee_credentials_template.csv"
                  : "/template/employees_template.csv"}
                download
              >
                Descargar plantilla CSV
              </a>
            </div>
          </div>
        )}

        {error && <p className={styles.error}>{error}</p>}
        <button type="button" className={styles.primaryButton} disabled={saving} onClick={createApplication}>
          {saving ? "Creando..." : "+ Crear aplicación"}
        </button>
      </div>
    </article>
  );
}

export default function BranchConfigurationPage() {
  const { idCompany, branchId } = useParams();
  const navigate = useNavigate();
  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(true);
  const [branchJobAreas, setBranchJobAreas] = useState([]);
  const [branchEmployeesFile, setBranchEmployeesFile] = useState(null);
  const [branchEmployeesCount, setBranchEmployeesCount] = useState(0);
  const [savingBranchAreas, setSavingBranchAreas] = useState(false);
  const [inbodyFile, setInbodyFile] = useState(null);
  const [inbodyPeriod, setInbodyPeriod] = useState(null);
  const [labFile, setLabFile] = useState(null);
  const [labPeriod, setLabPeriod] = useState(null);
  const [uploading, setUploading] = useState("");
  const [message, setMessage] = useState("");
  const [showEmployeeManager, setShowEmployeeManager] = useState(false);
  const [editingApplication, setEditingApplication] = useState(null);

  const loadBranch = useCallback(async () => {
    const [data, employeesCount] = await Promise.all([
      companyBranchesApi.getById(branchId),
      employeesApi.getEmployeeCountByBranch(branchId),
    ]);
    setBranch(data);
    setBranchJobAreas(data.jobAreas || []);
    setBranchEmployeesCount(employeesCount);
  }, [branchId]);

  useEffect(() => {
    loadBranch()
      .catch((error) => {
        console.error("Error cargando sucursal:", error);
        setMessage("No se pudo cargar la sucursal.");
      })
      .finally(() => setLoading(false));
  }, [loadBranch]);

  const saveBranchAreas = async () => {
    setSavingBranchAreas(true);
    try {
      await companyBranchesApi.update(branchId, { jobAreas: branchJobAreas });
      await loadBranch();
      setMessage("Áreas generales de la sucursal guardadas correctamente.");
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudieron guardar las áreas de la sucursal.");
    } finally {
      setSavingBranchAreas(false);
    }
  };

  const uploadBranchEmployees = async () => {
    if (!branchEmployeesFile) {
      setMessage("Selecciona el archivo CSV de empleados de la sucursal.");
      return;
    }

    setUploading("employees");
    try {
      const result = await employeesApi.batchCreateEmployees(branchId, branchEmployeesFile);
      const count = await employeesApi.getEmployeeCountByBranch(branchId);
      setBranchEmployeesCount(count);
      setBranchEmployeesFile(null);
      setMessage(
        `Empleados cargados: ${result.createdCount} nuevos y ${result.skippedCount} existentes.`
      );
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo cargar el archivo de empleados.");
    } finally {
      setUploading("");
    }
  };

  const uploadInbody = async () => {
    if (!inbodyFile || !inbodyPeriod) {
      setMessage("Selecciona archivo y periodo para InBody.");
      return;
    }
    setUploading("inbody");
    try {
      await inbodyApi.uploadInBody(branchId, inbodyFile, inbodyPeriod);
      setInbodyFile(null);
      setInbodyPeriod(null);
      setMessage("Archivo InBody cargado correctamente.");
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo cargar el archivo InBody.");
    } finally {
      setUploading("");
    }
  };

  const uploadLab = async () => {
    if (!labFile || !labPeriod) {
      setMessage("Selecciona archivo y periodo para laboratorio.");
      return;
    }
    setUploading("lab");
    try {
      await labResultsApi.upload(branchId, labFile, labPeriod);
      setLabFile(null);
      setLabPeriod(null);
      setMessage("Archivo de laboratorio cargado correctamente.");
    } catch (error) {
      setMessage(error.response?.data?.message || "No se pudo cargar el archivo de laboratorio.");
    } finally {
      setUploading("");
    }
  };

  if (loading) return <div className={styles.container}>Cargando sucursal...</div>;
  if (!branch) return <div className={styles.container}>{message}</div>;

  return (
    <div className={styles.container}>
      <button className={styles.backButton} onClick={() => navigate(`/company/${idCompany}`)}>← Volver a sucursales</button>
      <div className={styles.header}>
        <div>
          <span>Configuración de sucursal</span>
          <h1>{branch.name}</h1>
          <p>Administra las áreas, empleados, aplicaciones y archivos propios de esta sucursal.</p>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.secondaryButton} onClick={() => setShowEmployeeManager(true)}>
            Ver empleados
          </button>
          <button className={styles.doneButton} onClick={() => navigate(`/company/${idCompany}`)}>Terminar</button>
        </div>
      </div>

      {message && <div className={styles.message}>{message}</div>}

      <section>
        <h2 className={styles.sectionTitle}>Datos generales de la sucursal</h2>
        <div className={styles.fileGrid}>
          <article className={styles.fileCard}>
            <h3>Áreas de trabajo generales</h3>
            <p className={styles.fieldHelp}>
              Estas áreas se usan en los cuestionarios generales de la sucursal.
            </p>
            <AreaEditor
              areas={branchJobAreas}
              onChange={setBranchJobAreas}
              placeholder="Nueva área de la sucursal"
            />
            <button
              type="button"
              className={styles.primaryButton}
              disabled={savingBranchAreas}
              onClick={saveBranchAreas}
            >
              {savingBranchAreas ? "Guardando..." : "Guardar áreas"}
            </button>
          </article>

          <article className={styles.fileCard}>
            <h3>Empleados de la sucursal</h3>
            <p className={styles.fieldHelp}>
              {branchEmployeesCount} empleados registrados en el padrón general.
              Esta carga no modifica los empleados exclusivos de las aplicaciones NOM-035.
            </p>
            <input
              type="file"
              accept=".csv,text/csv"
              onChange={(event) => setBranchEmployeesFile(event.target.files[0] || null)}
            />
            <a className={styles.templateLink} href="/template/employees_template.csv" download>
              Descargar plantilla CSV
            </a>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={uploading === "employees" || !branchEmployeesFile}
              onClick={uploadBranchEmployees}
            >
              {uploading === "employees" ? "Subiendo..." : "Subir empleados"}
            </button>
          </article>
        </div>
      </section>

      <section>
        <h2 className={styles.sectionTitle}>Cuestionarios</h2>
        <div className={styles.surveyGrid}>
          {SURVEYS.map((survey) => (
            <SurveySection
              key={survey.name}
              survey={survey}
              branch={branch}
              branchEmployeesCount={branchEmployeesCount}
              onCreated={loadBranch}
              onEdit={setEditingApplication}
            />
          ))}
        </div>
      </section>

      <section className={styles.filesSection}>
        <h2 className={styles.sectionTitle}>Archivos de la sucursal</h2>
        <div className={styles.fileGrid}>
          <article className={styles.fileCard}>
            <h3>InBody</h3>
            <MonthYearPicker value={inbodyPeriod} onChange={setInbodyPeriod} placeholder="Mes y año" />
            <input type="file" accept=".xlsx" onChange={(event) => setInbodyFile(event.target.files[0] || null)} />
            <button className={styles.primaryButton} disabled={uploading === "inbody"} onClick={uploadInbody}>
              {uploading === "inbody" ? "Subiendo..." : "Subir InBody"}
            </button>
          </article>
          <article className={styles.fileCard}>
            <h3>Químicas sanguíneas</h3>
            <MonthYearPicker value={labPeriod} onChange={setLabPeriod} placeholder="Mes y año" />
            <input type="file" accept=".xlsx" onChange={(event) => setLabFile(event.target.files[0] || null)} />
            <button className={styles.primaryButton} disabled={uploading === "lab"} onClick={uploadLab}>
              {uploading === "lab" ? "Subiendo..." : "Subir laboratorio"}
            </button>
          </article>
        </div>
      </section>

      {showEmployeeManager && (
        <EmployeeManagerModal
          branch={branch}
          onClose={() => setShowEmployeeManager(false)}
          onBranchCountChange={setBranchEmployeesCount}
        />
      )}

      {editingApplication && (
        <ApplicationEditModal
          application={editingApplication}
          onClose={() => setEditingApplication(null)}
          onSaved={loadBranch}
        />
      )}
    </div>
  );
}
